/** Lon/lat ring in GeoJSON order: [longitude, latitude]. */
export type LonLat = [number, number];

export type PolygonRing = LonLat[];

export type LatLngPoint = { lat: number; lng: number };

/** Ensure the ring is closed (first point equals last) for GeoJSON Polygon. */
export function closeRing(ring: PolygonRing): PolygonRing {
  if (ring.length === 0) return ring;
  const [fx, fy] = ring[0];
  const [lx, ly] = ring[ring.length - 1];
  if (fx === lx && fy === ly) return ring;
  return [...ring, [fx, fy]];
}

/** Build a FeatureCollection of Polygon features from lon/lat rings (WGS84). */
export function polygonsToFeatureCollection(rings: PolygonRing[]): GeoJSON.FeatureCollection {
  const features: GeoJSON.Feature[] = rings
    .filter((ring) => ring.length >= 3)
    .map((ring, index) => ({
      type: "Feature",
      properties: {
        name: `Plot ${index + 1}`,
        source: "map-draw",
      },
      geometry: {
        type: "Polygon",
        coordinates: [closeRing(ring)],
      },
    }));

  return {
    type: "FeatureCollection",
    features,
  };
}

/** Convert Leaflet-style lat/lng rings to a GeoJSON FeatureCollection. */
export function latLngRingsToFeatureCollection(
  rings: LatLngPoint[][],
): GeoJSON.FeatureCollection {
  return polygonsToFeatureCollection(
    rings.map((ring) => ring.map((p) => [p.lng, p.lat] as LonLat)),
  );
}

/** Serialize a FeatureCollection as a GeoJSON File for the documents upload API. */
export function featureCollectionToGeoJsonFile(
  featureCollection: GeoJSON.FeatureCollection,
  filename = "plots-from-map.geojson",
): File {
  const body = JSON.stringify(featureCollection, null, 2);
  return new File([body], filename, { type: "application/geo+json" });
}

/** Convenience: lat/lng rings → GeoJSON File ready for geolocation upload. */
export function latLngRingsToGeoJsonFile(
  rings: LatLngPoint[][],
  filename = "plots-from-map.geojson",
): File {
  return featureCollectionToGeoJsonFile(latLngRingsToFeatureCollection(rings), filename);
}
